import {
  expandingSquare,
  parallelTrack,
  sectorSearch,
  type SearchPatternId,
  type SearchWaypoint,
} from "@nav-games/mob";

/// Schematic figure for a SAR search pattern — plots the leg
/// waypoints in nautical-mile coordinates and labels the datum / box.

interface Props {
  pattern: SearchPatternId;
  size?: number;
}

const VW = 320;
const VH = 320;
const STROKE = "var(--text, #0f172a)";
const MUTED = "var(--text-muted, #64748b)";
const LEG = "#1e6091";
const DATUM = "#c0382b";

function generateWaypoints(pattern: SearchPatternId): readonly SearchWaypoint[] {
  switch (pattern) {
    case "expanding-square":
      return expandingSquare(0.4, 14);
    case "sector-search":
      return sectorSearch(2);
    case "parallel-track":
      return parallelTrack(4, 5, 0.7);
    case "creeping-line":
      // Same generator as parallel-track but conceptually drift-perp.
      // For the diagram, plot a wider-shorter rectangle to suggest
      // perpendicular-to-drift legs.
      return parallelTrack(5, 4, 0.7);
  }
}

export function SearchPatternFigure({ pattern, size = VW }: Props) {
  const points = generateWaypoints(pattern);

  // Compute bounding box of the pattern with a small margin.
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs, 0);
  const maxX = Math.max(...xs, 0);
  const minY = Math.min(...ys, 0);
  const maxY = Math.max(...ys, 0);
  const range = Math.max(maxX - minX, maxY - minY) || 1;
  const pad = range * 0.15;
  const xRange = { lo: minX - pad, hi: maxX + pad };
  const yRange = { lo: minY - pad, hi: maxY + pad };

  const xScale = (VW - 40) / (xRange.hi - xRange.lo);
  const yScale = (VH - 60) / (yRange.hi - yRange.lo);
  const scale = Math.min(xScale, yScale);

  const project = (p: SearchWaypoint): { x: number; y: number } => ({
    x: 20 + (p.x - xRange.lo) * scale,
    y: VH - 40 - (p.y - yRange.lo) * scale,
  });

  const datumXY = project({ x: 0, y: 0 });

  const linePath = points
    .map((p, i) => {
      const proj = project(p);
      return `${i === 0 ? "M" : "L"} ${proj.x.toFixed(2)} ${proj.y.toFixed(2)}`;
    })
    .join(" ");

  return (
    <svg
      viewBox={`0 0 ${VW} ${VH}`}
      width={size}
      height={(size * VH) / VW}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={`Search pattern — ${pattern}`}
      style={{ maxWidth: "100%", height: "auto", display: "block" }}
    >
      <rect
        x={0}
        y={0}
        width={VW}
        height={VH}
        fill="var(--bg-elev, #f8fafc)"
        stroke={MUTED}
      />

      {/* Compass arrow — top right. */}
      <g transform={`translate(${VW - 40} 30)`}>
        <line x1={0} y1={0} x2={0} y2={-20} stroke={STROKE} strokeWidth={1.5} />
        <polygon points="-4,-17 0,-25 4,-17" fill={STROKE} />
        <text
          x={0}
          y={14}
          fontSize={11}
          textAnchor="middle"
          fontWeight={700}
          fill={STROKE}
        >
          N
        </text>
      </g>

      {/* Pattern path. */}
      <path
        d={linePath}
        fill="none"
        stroke={LEG}
        strokeWidth={1.8}
        strokeLinejoin="round"
      />

      {/* Waypoint dots. */}
      {points.map((p, i) => {
        const proj = project(p);
        return (
          <circle
            key={i}
            cx={proj.x}
            cy={proj.y}
            r={1.5}
            fill={LEG}
            opacity={0.5}
          />
        );
      })}

      {/* Datum marker. */}
      <circle
        cx={datumXY.x}
        cy={datumXY.y}
        r={5}
        fill={DATUM}
        stroke="white"
        strokeWidth={1.5}
      />
      <text
        x={datumXY.x + 8}
        y={datumXY.y + 4}
        fontSize={10}
        fontWeight={700}
        fill={DATUM}
      >
        Datum
      </text>

      {/* Footer caption. */}
      <text
        x={10}
        y={VH - 12}
        fontSize={10}
        fill={MUTED}
      >
        {pattern === "expanding-square"
          ? "Expanding-square (SS) — outward spiral from datum"
          : pattern === "sector-search"
            ? "Sector search (VS) — three triangles 120° apart"
            : pattern === "parallel-track"
              ? "Parallel-track (PS) — rectangular box, lawnmower legs"
              : "Creeping-line (CS) — legs perpendicular to drift direction"}
      </text>
    </svg>
  );
}
